import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { salesInvoiceService, SalesInvoiceListDTO, SalesInvoiceStatus } from '../../../../services/salesInvoiceService';
import { ArrowLeft, Search, Eye } from 'lucide-react';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import './SalesInvoicePage.css';

const STATUS_BADGE: Record<SalesInvoiceStatus, string> = {
  Draft:         'ims-badge ims-badge-draft',
  Issued:        'ims-badge ims-badge-submitted',
  PartiallyPaid: 'ims-badge ims-badge-partially-received',
  Paid:          'ims-badge ims-badge-completed',
  Overdue:       'ims-badge ims-badge-rejected',
  Voided:        'ims-badge ims-badge-closed',
  Cancelled:     'ims-badge ims-badge-closed',
};

const SalesInvoiceInquiryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const fmt      = useCurrencyFormat();

  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState<SalesInvoiceListDTO[] | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [searched,  setSearched]  = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmtDate  = (d: string) => new Date(d).toLocaleDateString(langCode);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setResults(null);
    setSearched(q);
    try {
      const res  = await salesInvoiceService.inquirySearch(q);
      const data = (res?.data as any)?.data ?? res?.data ?? [];
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 960, margin: '0 auto' }}>

        <div className="ims-top-bar" style={{ marginBottom: 24 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/sales/sales-invoices')}>
              <ArrowLeft size={13} /> {t('salesInvoice.backToList')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('salesInvoice.inquiryTitle')}</h1>
              <p className="ims-page-subtitle">{t('salesInvoice.inquirySubtitle')}</p>
            </div>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className="ims-card" style={{ marginBottom: 24 }}>
          <div className="ims-card-body" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ims-text-muted)', pointerEvents: 'none' }} />
                <input
                  ref={inputRef}
                  type="text"
                  className="ims-form-control"
                  style={{ paddingInlineStart: 36 }}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('salesInvoice.inquiryPlaceholder')}
                  autoFocus
                />
              </div>
              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={loading || !query.trim()}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : <Search size={15} />}
                {t('salesInvoice.inquirySearch')}
              </button>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--ims-text-muted)' }}>
              {t('salesInvoice.inquiryHint')}
            </p>
          </div>
        </div>

        {/* ── Results ── */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        )}

        {!loading && results !== null && results.length === 0 && (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('salesInvoice.inquiryNoResults', { query: searched })}
          </div>
        )}

        {!loading && results && results.length > 0 && (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 140 }}>{t('salesInvoice.invoiceNumber')}</th>
                    <th className="ims-th" style={{ width: 140 }}>{t('salesInvoice.invoiceReferenceNumber')}</th>
                    <th className="ims-th">{t('salesInvoice.customer')}</th>
                    <th className="ims-th" style={{ width: 110 }}>{t('salesInvoice.invoiceDate')}</th>
                    <th className="ims-th" style={{ width: 120 }}>{t('common.status')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('salesInvoice.totalAmount')}</th>
                    <th className="ims-th" style={{ width: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {results.map((inv) => (
                    <tr key={inv.id} className="ims-tr">
                      <td className="ims-td"><code>{inv.invoiceNumber}</code></td>
                      <td className="ims-td">
                        {inv.referenceNumber
                          ? <code style={{ fontSize: 11 }}>{inv.referenceNumber}</code>
                          : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>}
                      </td>
                      <td className="ims-td"><strong>{inv.customerName}</strong></td>
                      <td className="ims-td">{fmtDate(inv.invoiceDate)}</td>
                      <td className="ims-td">
                        <span className={STATUS_BADGE[inv.status] ?? 'ims-badge ims-badge-draft'}>
                          {t(`salesInvoice.status${inv.status}`)}
                        </span>
                      </td>
                      <td className="ims-td ims-td--line-total">
                        <strong>{fmt(inv.totalAmount)}</strong>
                      </td>
                      <td className="ims-td">
                        <button
                          className="grid-action-btn-view"
                          title={t('common.view')}
                          onClick={() => navigate(`/sales/sales-invoices/${inv.id}`)}
                        >
                          <Eye size={16} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SalesInvoiceInquiryPage;
